import { Form, Input } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { Card, Button, Loader } from '../../components/ui';
import { changePassword } from '../../api/password.api';
import { logout as logoutApi } from '../../api/auth.api';
import { useAuthStore } from '../../stores/auth.store';
import { toast, handleMutationError } from '../../lib/notification';
import { PasswordChecklist } from './components/PasswordChecklist';

interface ChangePasswordFormValues {
	currentPassword: string;
	newPassword: string;
	confirmNewPassword: string;
}

/**
 * Standalone change-password page. Works in two modes:
 *
 * - **Forced** (`enforcePasswordChange` is true): full-screen, no back button,
 *   logs out on success so the user re-authenticates with the new password.
 * - **Voluntary** (navigated from /profile or directly): shows a back button,
 *   logs out on success (BFF invalidates the session after a password change).
 */
export default function ChangePasswordPage() {
	const navigate = useNavigate();
	const { isAuthenticated, isLoading, enforcePasswordChange } = useAuth();
	const authStore = useAuthStore();
	const [form] = Form.useForm<ChangePasswordFormValues>();
	const newPassword = Form.useWatch('newPassword', form) ?? '';

	const isForced = !!enforcePasswordChange;

	const mutation = useMutation({
		mutationFn: changePassword,
		onSuccess: async () => {
			toast.success('Password changed successfully. Please login again.');
			try {
				await logoutApi();
			} catch {
				// Ignore logout errors — cookies may already be partially cleared
			}
			authStore.logout();
			navigate('/login', { replace: true });
		},
		onError: (error: unknown) => {
			handleMutationError(error, 'Failed to change password');
		},
	});

	const handleSubmit = (values: ChangePasswordFormValues) => {
		mutation.mutate({
			currentPassword: values.currentPassword,
			newPassword: values.newPassword,
			confirmNewPassword: values.confirmNewPassword,
		});
	};

	if (isLoading) {
		return <Loader variant="fullscreen" />;
	}

	if (!isAuthenticated) {
		return <Navigate to="/login" replace />;
	}

	return (
		<div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
			<div className="w-full max-w-md">
				<Card>
					<div className="mb-6 text-center">
						<h1 className="text-2xl font-semibold text-gray-900">Change Your Password</h1>
						<p className="mt-2 text-sm text-gray-500">
							{isForced
								? 'You are required to change your password before continuing.'
								: 'Enter your current password and choose a new one.'}
						</p>
					</div>

					<Form
						form={form}
						name="change-password"
						layout="vertical"
						onFinish={handleSubmit}
						autoComplete="off"
						size="large"
					>
						<Form.Item
							name="currentPassword"
							label="Current Password"
							rules={[{ required: true, message: 'Please enter your current password' }]}
						>
							<Input.Password
								prefix={<LockOutlined />}
								placeholder="Enter current password"
								autoComplete="current-password"
							/>
						</Form.Item>

						<Form.Item
							name="newPassword"
							label="New Password"
							rules={[
								{ required: true, message: 'Please enter a new password' },
								{ min: 8, message: 'Password must be at least 8 characters' },
								{
									pattern: /[A-Z]/,
									message: 'Must contain an uppercase letter',
								},
								{
									pattern: /[a-z]/,
									message: 'Must contain a lowercase letter',
								},
								{
									pattern: /[0-9]/,
									message: 'Must contain a number',
								},
								{
									pattern: /[^A-Za-z0-9]/,
									message: 'Must contain a special character',
								},
							]}
						>
							<Input.Password
								prefix={<LockOutlined />}
								placeholder="Enter new password"
								autoComplete="new-password"
							/>
						</Form.Item>

						<PasswordChecklist password={newPassword} />

						<Form.Item
							name="confirmNewPassword"
							label="Confirm New Password"
							dependencies={['newPassword']}
							rules={[
								{ required: true, message: 'Please confirm your new password' },
								({ getFieldValue }) => ({
									validator(_, value) {
										if (!value || getFieldValue('newPassword') === value) {
											return Promise.resolve();
										}
										return Promise.reject(new Error('Passwords do not match'));
									},
								}),
							]}
						>
							<Input.Password
								prefix={<LockOutlined />}
								placeholder="Confirm new password"
								autoComplete="new-password"
							/>
						</Form.Item>

						<Form.Item className="mt-6 mb-0">
							<Button
								variant="primary"
								fullWidth
								loading={mutation.isPending}
								htmlType="submit"
							>
								{mutation.isPending ? 'Changing Password...' : 'Change Password'}
							</Button>
						</Form.Item>

						{!isForced && (
							<div className="mt-3 text-center">
								<Button
									variant="text"
									onClick={() => navigate('/profile')}
								>
									Back to Profile
								</Button>
							</div>
						)}
					</Form>
				</Card>
			</div>
		</div>
	);
}
